import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Form, Input, Button,
    Card, Spin, Typography, Divider, Row, Col,
} from 'antd';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import { getPropertyConfig, updatePdfContent } from '../apiCalls';
import PageLayout from './PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { getCurrencySymbol } from '../utils/currencyUtils';

const { Title, Text } = Typography;

const cardStyle = { marginBottom: 24 };

const CostTableManager = () => {
    const { propertyID } = useParams();
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [propertyConfig, setPropertyConfig] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const pageLayoutProps = usePageLayout();

    const fetchData = useCallback(async () => {
        setFetchLoading(true);
        try {
            const config = await getPropertyConfig(propertyID);
            if (config) {
                setPropertyConfig(config);
                if (config.pdfContent) {
                    form.setFieldsValue(config.pdfContent);
                }
            }
        } catch (err) {
            enqueueSnackbar(t('pdf.costTable.error.loading'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setFetchLoading(false);
        }
    }, [propertyID, form, enqueueSnackbar, t]);

    useEffect(() => {
        if (propertyID) {
            fetchData();
        }
    }, [propertyID, fetchData]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            await updatePdfContent(propertyID, values);
            enqueueSnackbar(t('pdf.costTable.success'), { variant: 'success' });
        } catch (err) {
            const errorMsg = err.response?.data || t('pdf.costTable.error.saving');
            enqueueSnackbar(errorMsg, { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 900, margin: '0 auto' }}>
                <Title level={2} style={{ marginBottom: 8 }}>{t('pdf.costTable.title')}</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 32 }}>
                    {t('pdf.costTable.subtitle')}
                </Text>

                <Spin spinning={fetchLoading}>
                    <Form form={form} onFinish={handleSubmit} layout="vertical">

                        {/* Cost Line Items */}
                        <Card title={t('pdf.costTable.costItems')} style={cardStyle}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                {t('pdf.costTable.costItemsDesc')}
                            </Text>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="PV Modules label" name={['costLabels', 'pvModules']}>
                                        <Input placeholder="e.g. PV Modules" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Mounting Kit label" name={['costLabels', 'mountingKit']}>
                                        <Input placeholder="e.g. Mounting Kit" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Components label" name={['costLabels', 'components']}>
                                        <Input placeholder="e.g. Components" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Labour label" name={['costLabels', 'labour']}>
                                        <Input placeholder="e.g. Labour" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Scaffolding label" name={['costLabels', 'scaffolding']}>
                                        <Input placeholder="e.g. Scaffolding" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="MCS Registration label" name={['costLabels', 'mcsRegistration']}>
                                        <Input placeholder="e.g. MCS Registration" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Building Control label" name={['costLabels', 'buildingControl']}>
                                        <Input placeholder="e.g. Building Control Fee" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="EV Charger label" name={['costLabels', 'evCharger']}>
                                        <Input placeholder="e.g. Single EV charging point" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Hot Water Diverter label" name={['costLabels', 'hotWaterDiverter']}>
                                        <Input placeholder="e.g. Hot water diverter" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="5kWh Battery label" name={['costLabels', 'battery5kWh']}>
                                        <Input placeholder="e.g. Battery 5kWh" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="10kWh Battery label" name={['costLabels', 'battery10kWh']}>
                                        <Input placeholder="e.g. Battery 10kWh" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Installer hourly rate" name={['costLabels', 'installerHourlyRate']}>
                                        <Input placeholder={`e.g. ${getCurrencySymbol(propertyConfig?.currency || 'GBP')}35`} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Table Headers */}
                        <Card title={t('pdf.costTable.columnHeaders')} style={cardStyle}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                {t('pdf.costTable.columnHeadersDesc')}
                            </Text>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Goods description header" name={['tableHeaders', 'goodsDescription']}>
                                        <Input placeholder="e.g. Description of goods" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Services description header" name={['tableHeaders', 'servicesDescription']}>
                                        <Input placeholder="e.g. Description of services" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Quantity header" name={['tableHeaders', 'quantity']}>
                                        <Input placeholder="e.g. Quantity" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Line Total header" name={['tableHeaders', 'lineTotal']}>
                                        <Input placeholder="e.g. Line Total" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Subtotal (ex VAT) header" name={['tableHeaders', 'subtotalExVat']}>
                                        <Input placeholder="e.g. Sub-total (ex VAT)" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="VAT label" name={['tableHeaders', 'vat']}>
                                        <Input placeholder="e.g. VAT @ {vat}%" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={24}>
                                    <Form.Item label="Grand Total header" name={['tableHeaders', 'grandTotal']}>
                                        <Input placeholder="e.g. GRAND TOTAL (inc VAT)" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Page Headings */}
                        <Card title={t('pdf.costTable.pageHeadings')} style={cardStyle}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                {t('pdf.costTable.pageHeadingsDesc')}
                            </Text>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Quotation heading" name={['pageHeadings', 'quotation']}>
                                        <Input placeholder="e.g. Quotation" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Payment Terms heading" name={['pageHeadings', 'paymentTerms']}>
                                        <Input placeholder="e.g. PAYMENT TERMS" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Notes heading" name={['pageHeadings', 'notes']}>
                                        <Input placeholder="e.g. NOTES:" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Details heading" name={['pageHeadings', 'details']}>
                                        <Input placeholder="e.g. Details for your location" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Inclusions/Exclusions heading" name={['pageHeadings', 'inclusionsExclusions']}>
                                        <Input placeholder="e.g. INCLUSIONS AND EXCLUSIONS" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Notable Exclusions heading" name={['pageHeadings', 'notableExclusions']}>
                                        <Input placeholder="e.g. Notable Exclusions/Requirements" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Field Labels */}
                        <Card title={t('pdf.costTable.formFields')} style={cardStyle}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                {t('pdf.costTable.formFieldsDesc')}
                            </Text>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Signature label" name={['fieldLabels', 'signature']}>
                                        <Input placeholder="e.g. Signature" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Date label" name={['fieldLabels', 'date']}>
                                        <Input placeholder="e.g. Date" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Standard Inverter label" name={['fieldLabels', 'inverterStandard']}>
                                        <Input placeholder="e.g. Inverter - Standard" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Hybrid Inverter label" name={['fieldLabels', 'inverterHybrid']}>
                                        <Input placeholder="e.g. Inverter - Hybrid" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        <Divider />

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                size="large"
                                style={{ minWidth: 200 }}
                            >
                                {loading ? t('common.saving') : t('common.save')}
                            </Button>
                        </Form.Item>

                    </Form>
                </Spin>
            </div>
        </PageLayout>
    );
};

export default CostTableManager;
